import { useState } from "react"
import { useTranslation } from "react-i18next"

import { surfaceColor, surfaceText } from "../../../constants/surface-tokens"
import { NotificationContentCard } from "../../../components/notifications/NotificationContentCard"
import { contentDestination } from "../../../components/notifications/contentDestination"
import { legacyContent } from "../helpers/legacyContent"
import { automationHref, notificationContent } from "../helpers/notificationContent"
import type { Notification } from "../types/notification.types.ts"

interface NotificationsListCardProps {
  notification: Notification
  onMarkAsRead: (id: number) => void
  onCardClick: (notification: Notification) => void
  onArchive: (id: number) => void
  density?: "compact" | "comfortable"
  showSecondaryActions?: boolean
}

export function NotificationsListCard({
  notification,
  onMarkAsRead,
  onCardClick,
  onArchive,
  density = "comfortable",
  showSecondaryActions = true
}: NotificationsListCardProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const members = notification?.group_members ?? []
  const isGrouped = (notification?.group_count ?? 1) > 1

  // A backend that predates the content block still has a record and a
  // destination worth showing; `legacyContent` derives what it can rather
  // than dropping the row back to a bare sentence.
  const content = notificationContent(notification)

  return (
    <div
      data-testid={`notification-${notification?.id}`}
      style={{
        background: surfaceColor.surface,
        borderBottom: `1px solid ${surfaceColor.border}`
      }}
    >
      <NotificationContentCard
        content={content}
        timestamp={notification?.timestamp}
        unread={notification?.unread}
        level={notification?.level}
        density={density}
        fallbackText={notification?.description}
        groupCount={notification?.group_count}
        onOpen={() => onCardClick(notification)}
        onMarkAsRead={() => onMarkAsRead(notification?.id)}
        onArchive={() => onArchive(notification?.id)}
        showSecondaryActions={showSecondaryActions}
        testId={`notification-card-${notification?.id}`}
      />

      {isGrouped && (
        <div style={{ padding: "0 16px 12px 68px" }}>
          <button
            type="button"
            data-testid={`group-count-${notification?.id}`}
            aria-expanded={expanded}
            aria-label={t("notifications.bell.showUpdates", {
              count: notification?.group_count
            })}
            onClick={(event) => {
              event.stopPropagation()
              setExpanded((open) => !open)
            }}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              ...surfaceText.label,
              fontWeight: 600,
              color: surfaceColor.teal
            }}
          >
            {/* The number stays visible whatever the label says: how many
                updates a row stands for is the fact, the wording is dressing. */}
            {notification?.group_count}{" "}
            {expanded
              ? t("notifications.bell.hideUpdates", "Hide earlier updates")
              : t("notifications.bell.showUpdates", {
                  count: notification?.group_count
                })}
          </button>

          {expanded && (
            <ul style={{ listStyle: "none", margin: "8px 0 0", padding: 0 }}>
              {members.map((member) => {
                const memberContent = member?.content ?? legacyContent(member)
                return (
                  <li
                    key={member?.id}
                    data-testid={`group-member-${member?.id}`}
                    style={{
                      ...surfaceText.meta,
                      color: surfaceColor.inkMuted,
                      paddingLeft: 10,
                      borderLeft: `2px solid ${surfaceColor.border}`,
                      marginBottom: 4
                    }}
                  >
                    {/* The subject, never the record: a group is keyed on
                        the record, so every member names the same one and
                        listing it three times says nothing. */}
                    {memberContent?.subject?.label || member?.description}
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

/** Whether a row offers anywhere to go — used to decide the cursor upstream. */
export const notificationHasDestination = (notification: Notification) =>
  Boolean(contentDestination(notification?.content?.action?.target)) ||
  !!automationHref(notification)
